import { observable, computed, action } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { AppRegistry, ComponentRegistry, ModelRegistry } from 'core/registry';

import { Model, App, ArrayApp } from 'core';
import { analytics } from 'core/analytics';

import { stripeStore } from 'apps/payment/stripe';
import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction';
import { countriesValues } from 'apps/payment/countries';
import { PermissionedModel } from 'apps/permissions/app';
import { FormStore } from 'libs/forms/app';
import { isObject } from 'lodash';

// amounts are expected in "cents"
// e.g. to get €1.50 amount would be 150
export const formatAmount = (amount, currency = '€') => {
  const clampedAmount = Math.max(amount, 0);
  const amountString = !clampedAmount
    ? '0.00'
    : (clampedAmount / 100).toFixed(2);
  return `${currency}${amountString}`;
};

export class PlanModel extends Model {
  static registryName = 'PlanModel';
  @observable name;
  @observable isLegacy;
}

export class PlanApp extends App {
  static registryName = 'PlanApp';
  static Model = PlanModel;
}

export class SubscriptionModel extends PermissionedModel {
  static registryName = 'SubscriptionModel';
  static ModelProps = {
    plan: PlanModel,
    seats: 'SeatsUsageModel',
    addSeatsPreview: 'SubscriptionPreviewModel',
    removeSeatsPreview: 'SubscriptionPreviewModel',
  };

  @observable scaSecret;
  @observable plan;
  @observable subscriptionId;
  @observable status; // ACTIVE, CANCELED, INCOMPLETE
  @observable currentPeriodEnd;
  @observable discount;
  @observable cancelAtPeriodEnd;
  @observable credits;
  @observable nextAmount;
  @observable initialAmount;
  @observable country;
  @observable vat;
  @observable seats;
  @observable addSeatsPreview;
  @observable removeSeatsPreview;
  @observable hasProSubscription;

  @computed get displayNextAmount() {
    return formatAmount(this.nextAmount);
  }

  @computed get isCancelled() {
    return (
      this.cancelAtPeriodEnd ||
      this.status === 'CANCELED' ||
      this.status === 'INCOMPLETE'
    );
  }

  @computed get isBillingCancelledInStripe() {
    return this.status === 'CANCELED' || this.status === 'INCOMPLETE';
  }

  @computed get isGrandFatherPlan() {
    return !!(this.plan && this.plan.name.includes('Founding'));
  }
}
ModelRegistry.add(SubscriptionModel);

export class SubscriptionApp extends App {
  static registryName = 'SubscriptionApp';
  static AppProps = {
    plan: PlanApp,
    seats: 'SeatsUsageApp',
    addSeatsPreview: 'SubscriptionPreviewApp',
    removeSeatsPreview: 'SubscriptionPreviewApp',
  };

  static Model = SubscriptionModel;

  @computed get requiresSCAValidation() {
    return !!this.scaSecret;
  }
}

AppRegistry.add(SubscriptionApp);

export class SubscriptionsArrayApp extends ArrayApp {
  static registryName = 'SubscriptionsArrayApp';
  static ElementApp = SubscriptionApp;
}
AppRegistry.add(SubscriptionsArrayApp);

export class CreditCardModel extends PermissionedModel {
  static registryName = 'CreditCardModel';
  @observable brand;
  @observable country;
  @observable stripeId;
  @observable name = '';
  @observable expMonth;
  @observable expYear;
  @observable lastDigits;
  @observable isDefault;

  @observable addressInfo;
}
ModelRegistry.add(CreditCardModel);

export class CreditCardApp extends App {
  static registryName = 'CreditCardApp';
  static Model = CreditCardModel;

  @observable submitting = false;

  @action OnAddError = () => {
    Alert.error(
      'An error ocurred when adding a payment method. Please, try again later.',
    );
    this.SetSubmitting(false);
  };

  @action UpdatePartialAddress = partialValue => {
    if (isObject(partialValue)) {
      const { value, ...extraInfo } = this.addressInfo || {};
      this.addressInfo = {
        ...extraInfo,
        value: {
          ...(value || {}),
          ...partialValue,
        },
      };
    }
  };

  @action SetName = name => {
    this.name = name;
    this.UpdatePartialAddress({ name });
  };

  @action SetAddressInfo = addressInfo => {
    if (isObject(addressInfo)) {
      this.addressInfo = {
        ...addressInfo,
        value: {
          address: { ...(addressInfo.value?.address || {}) },
          name: this.name,
        },
      };
    }
  };

  @action SetSubmitting = bool => {
    this.submitting = bool;
  };

  @action MakeDefault = () => {
    const options = { onSuccess: this.manager.NewCreditCardsQuery };
    this.manager.MakeDefaultPaymentMethod(this, options);
  };

  @action DeleteCardError = () => {
    Alert.error(
      'An error ocurred when deleting card. Please, try again later.',
    );
    this.SetSubmitting(false);
  };

  @action ConfirmDeleteCard = ({ successCallback }) => {
    this.SetSubmitting(true);
    this.manager.DeletePaymentMethod(this, {
      onSuccess: () => {
        if (this.manager) {
          this.manager.NewCreditCardsQuery();
        }
        this.SetSubmitting(false);
        successCallback();
      },
      onSysErr: this.DeleteCardError,
      onUserErr: this.DeleteCardError,
    });
  };

  @action ConfirmDelete = () => {
    const options = {
      onSuccess: this.manager && this.manager.NewCreditCardsQuery,
    };
    modalStore.SetModalComponent(ConfirmAction, {
      small: true,
      callBack: () => this.manager.DeletePaymentMethod(this, options),
      title: 'delete this card?',
      undo: false,
      actionName: 'Delete',
    });
  };

  async Submit(stripe, elements) {
    const cardElement = elements.getElement('cardNumber');

    const isNotComplete =
      !this.name || !this.addressInfo.complete || this.addressInfo.empty;

    if (isNotComplete) {
      Alert.error('Please fill in the required fields');
      return;
    }

    this.SetSubmitting(true);

    if (!stripe) {
      this.OnStripeError({
        type: 'No Stripe',
        code: 'no-stripe',
        message: 'Stripe object has not been provided',
      });
      this.SetSubmitting(true);
      return;
    }

    try {
      // address_postal_code need map to address_zip for stripe api
      const keyMap = {
        postal_code: 'zip',
      };

      const addressEntries = Object.entries(this.addressInfo.value.address);
      const addressPayload = addressEntries.reduce((acc, [key, value]) => {
        return {
          ...acc,
          [`address_${keyMap[key] || key}`]: value || '',
        };
      }, {});

      // Arbitrarily choose to use 'cardNumber' to create the token.
      // Could be any stripe Element in the form.
      const { error, token } = await stripe.createToken(cardElement, {
        ...addressPayload,
        name: this.name,
      });
      if (error) {
        this.OnStripeError(error);
        return;
      }
      analytics.sendEvent(
        'AddPaymentInfo',
        {},
        { twitter: true, facebook: true },
      );

      analytics.linkedinPixel('1169169');
      const options = {
        onSuccess: this.manager.NewCreditCardsQuery,
        onUserErr: this.OnAddError,
      };

      this.manager.AddPaymentMethod(token.id, options);
    } catch (error) {
      this.SetSubmitting(false);

      Alert.error(error.message);
    }
  }

  @action OnStripeError = error => {
    this.SetSubmitting(false);
    const { type, code, message } = error;
    // TODO - Display the error messages somewhere in the wizard?
    const scopingFunction = scope => {
      scope.setExtra('type', type);
      scope.setExtra('code', code);
    };

    if (message) {
      Alert.error(message);
    }

    analytics.logError(message, scopingFunction);
  };

  @action OnUserError = errors => {
    const billingError = errors.find(
      ({ code }) => code === 'BILLING_CARD_DECLINED',
    );
    Alert.error(billingError.message);
  };
}
AppRegistry.add(CreditCardApp);

export class CreditCardArrayApp extends ArrayApp {
  static registryName = 'CreditCardArrayApp';
  static ElementApp = CreditCardApp;
}
AppRegistry.add(CreditCardArrayApp);

export class InvoiceModel extends Model {
  static registryName = 'InvoiceModel';
  @observable status;
  @observable number;
  @observable periodStart;
  @observable periodEnd;
  @observable paymentMethod;
  @observable amount;
  @observable downloadUrl;
}
ModelRegistry.add(InvoiceModel);

export class InvoiceApp extends App {
  static registryName = 'InvoiceApp';
  static Model = InvoiceModel;

  @computed get displayAmount() {
    return formatAmount(this.amount);
  }

  @computed get statusIcon() {
    // See https://stripe.com/docs/billing/invoices/workflow#workflow-overview
    switch (this.status) {
      case 'DRAFT': {
        return '📝';
      }
      case 'OPEN': {
        return '🔄';
      }
      case 'PAID': {
        return '✅';
      }
      case 'VOID': {
        return '⛔️';
      }
      case 'UNCOLLECTIBLE': {
        return '❌';
      }
      default:
        return '';
    }
  }
}
AppRegistry.add(InvoiceApp);

export class InvoiceArrayApp extends ArrayApp {
  static registryName = 'InvoiceArrayApp';
  static ElementApp = InvoiceApp;
}
AppRegistry.add(InvoiceArrayApp);

export class SeatsUsageModel extends Model {
  static registryName = 'SeatsUsageModel';
  @observable usedSpaceSeats = 0;
  @observable usedProjectsSeats = 0;
  @observable usedInvitesSeats = 0;
  @observable unusedSeats = 0;
  @observable includedPlanSeats = 0;
  @observable purchasedSeats = 0;
  @observable totalSubSeats = 0;
  @observable freeSeats = 0;
  @observable seatsChange = 0;
  @observable loading = false;

  @computed get usedSeats() {
    return this.usedSpaceSeats + this.usedProjectsSeats + this.usedInvitesSeats;
  }

  // only makes sense in the context of ACTIVATING A NEW SUBSCRIPTION
  @computed get requiredExtraSeats() {
    return Math.max(this.usedSeats - this.includedPlanSeats, 0);
  }

  @computed get optionalExtraSeats() {
    return Math.max(
      this.totalSubSeats - this.includedPlanSeats - this.requiredExtraSeats,
      0,
    );
  }

  @computed get canBeAdded() {
    return true;
  }

  @computed get canBeRemoved() {
    return this.optionalExtraSeats > 0;
  }
}
ModelRegistry.add(SeatsUsageModel);

export class SeatsUsageApp extends App {
  static registryName = 'SeatsUsageApp';
  static Model = SeatsUsageModel;

  @observable formVisible = null;
  @observable addSeatsAmount = 0;
  @observable removeSeatsAmount = '';
  @observable validationError = false;

  @computed get newTotalSeats() {
    return (
      this.totalSubSeats +
      (parseInt(this.addSeatsAmount, 10) || 0) -
      (parseInt(this.removeSeatsAmount, 10) || 0)
    );
  }

  @computed get newOptionalExtraSeats() {
    return (
      this.optionalExtraSeats +
      (parseInt(this.addSeatsAmount, 10) || 0) -
      (parseInt(this.removeSeatsAmount, 10) || 0)
    );
  }

  @action SetFormVisibility = mode => {
    if (!['add', 'remove', null].includes(mode)) return;
    if (mode === 'add' && !this.canBeAdded) {
      Alert.error('Please add a payment method, before purchasing more seats.');
      return;
    }
    if (mode === 'remove' && !this.canBeRemoved) {
      Alert.error(
        "You don't have any free seats to remove. Plase, free some seats before trying again",
      );
      return;
    }
    if (this.formVisible === mode) {
      this.formVisible = null;
      return;
    }
    this.formVisible = mode;
    this.addSeatsAmount = '';
    this.removeSeatsAmount = '';
  };

  @action Update = (type, value, options = { refresh: false }) => {
    const { refresh } = options;
    const reg = new RegExp('^[0-9]+?$');
    if (value && !reg.test(value)) {
      return;
    }
    if (type === 'add') {
      const inputValue = parseInt(value, 10);

      this.addSeatsAmount = Number.isNaN(inputValue) ? '' : inputValue;
      if (refresh) {
        this.manager.NewPreviewExtraSeatsQuery(this.addSeatsAmount || 0, this);
      } else {
        this.manager.NewPreviewAlterSeatsQuery(this.addSeatsAmount, 0);
      }
    }
    if (type === 'remove') {
      const parsedValue = parseInt(value, 10);
      this.validationError = parsedValue > this.optionalExtraSeats;
      this.removeSeatsAmount =
        Math.min(parsedValue, this.optionalExtraSeats) || '';
      this.manager.NewPreviewAlterSeatsQuery(0, this.removeSeatsAmount);
    }
  };

  @action OnAlterSeatsError = () => {
    Alert.error(
      "An error ocurred when modifying your subscription's seats. Please, try again later.",
    );
    this.loading = false;
  };

  @action OnAlterSeatsSuccess = () => {
    this.SetFormVisibility(null);
    Alert.success('Your subscription seats have been modified sucessfully.');
  };

  @action RemoveSeats = (removeNumber, successCallback) => {
    this.loading = true;
    this.manager.removeUnassignedSeats(removeNumber, {
      onSysErr: this.OnAlterSeatsError,
      onUserErr: this.OnAlterSeatsError,
      onSuccess: result => {
        this.OnAlterSeatsSuccess(result);
        this.manager.NewSubscriptionQuery();
        this.loading = false;
        if (successCallback) {
          successCallback();
        }
      },
    });
  };

  @action ConfirmRemoveSeats = removeNumber => {
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () =>
        this.manager.removeUnassignedSeats(removeNumber, {
          onSysErr: this.OnAlterSeatsError,
          onUserErr: this.OnAlterSeatsError,
          onSuccess: result => {
            this.OnAlterSeatsSuccess(result);
            this.manager.NewSubscriptionQuery();
            modalStore.Close();
          },
        }),
      title: 'Are you sure about removing unassigned seats? ',
      actionName: 'Proceed',
      undo: true,
    });
  };

  @action ConfirmAddSeats = option => {
    this.loading = true;
    this.manager.SaveAlterSeats(this, {
      onSysErr: this.OnAlterSeatsError,
      onUserErr: this.OnAlterSeatsError,
      onSuccess: result => {
        this.OnAlterSeatsSuccess(result);
        this.manager.NewSubscriptionQuery();
        this.manager.addSeatsPreviewResult = null;

        modalStore.Close();
        if (option && option.successCallback) {
          option.successCallback();
        }
        this.loading = false;
      },
    });
  };

  @action ConfirmAlter = (addSeatsAmount, removeSeatsAmount) => {
    this.manager.NewPreviewAlterSeatsQuery(
      addSeatsAmount || 0,
      removeSeatsAmount || 0,
    );
    const ConfirmSubscription = ComponentRegistry.get(
      'modals.ConfirmSubscription',
    );
    modalStore.SetModalComponent(ConfirmSubscription, {
      appManager: this.manager,
      // FIXME - uses paymentSpacePreview from manager instead of "this"
      confirmAction: () => {
        this.manager.paymentSpacePreview.SetSubscribing(true);
        this.manager.SaveAlterSeats(this, {
          onSysErr: this.OnAlterSeatsError,
          onUserErr: this.OnAlterSeatsError,
          onSuccess: result => {
            this.OnAlterSeatsSuccess(result);
            this.manager.NewSubscriptionQuery();
            this.manager.paymentSpacePreview.SetSubscribing(false);
            modalStore.Close();
          },
        });
      },
    });
  };

  @action ConfirmAdd = () => {
    this.ConfirmAlter(this.addSeatsAmount, 0);
  };

  @action ConfirmRemove = () => {
    this.ConfirmAlter(0, this.removeSeatsAmount);
  };
}
AppRegistry.add(SeatsUsageApp);

export class SubscriptionPreviewModel extends Model {
  static registryName = 'SubscriptionPreviewModel';
  static ModelProps = {
    seats: SeatsUsageModel,
  };

  @observable totalAmount;
  @observable perSeatAmount;
  @observable initialAmount;
  @observable billingStarts;
  @observable prorationAmount;
  @observable prorationTimestamp;
  @observable discountAmount;
  @observable seats;
  @observable loading;

  @computed get discountApplied() {
    return this.discountAmount > 0;
  }

  @computed get usedExtraAmount() {
    return this.seats.usedExtraSeats * this.perSeatAmount;
  }

  @computed get optionalExtraAmount() {
    return this.seats.optionalExtraSeats * this.perSeatAmount;
  }

  @computed get finalAmount() {
    return Math.max(this.totalAmount - this.discountAmount, 0);
  }

  @computed get diffAmount() {
    return Math.abs(this.seats.seatsChange) * this.perSeatAmount;
  }

  @computed get displayUsedExtraAmount() {
    return formatAmount(this.usedExtraAmount);
  }

  @computed get displayOptionalExtraAmount() {
    return formatAmount(this.optionalExtraAmount);
  }

  @computed get displayTotalAmount() {
    return formatAmount(this.totalAmount);
  }

  @computed get displayFinalAmount() {
    return formatAmount(this.finalAmount);
  }

  @computed get displayDiffAmount() {
    return formatAmount(this.diffAmount);
  }

  @computed get displayInitialAmount() {
    return formatAmount(this.initialAmount);
  }

  @computed get displayPerSeatAmount() {
    return formatAmount(this.perSeatAmount);
  }
}
ModelRegistry.add(SubscriptionPreviewModel);

export class SubscriptionPreviewApp extends App {
  static registryName = 'SubscriptionPreviewApp';
  static AppProps = {
    seats: SeatsUsageApp,
  };

  static Model = SubscriptionPreviewModel;
}
AppRegistry.add(SubscriptionPreviewApp);

export class PaymentSpaceModel extends Model {
  static registryName = 'PaymentSpaceModel';
  static ModelProps = {
    billingSubscriptions: ['SubscriptionModel'],
    subscriptionPreview: 'SubscriptionPreviewModel',
    creditCards: ['CreditCardModel'],
    seatsUsage: 'SeatsUsageModel',
    invoices: ['InvoiceModel'],
  };

  @observable __typename = '';
  @observable username = '';
  @observable slug = '';
  @observable billingSubscriptions = [];
  @observable subscriptionPreview = null;
  @observable creditCards = [];
  @observable seatsUsage = null;
  @observable invoices = [];
  @observable countryCode = '';
  @observable taxId = '';
  @observable hasBillingPermissions = false;
  @observable hasValidPaymentMethod = false;
  @observable hasProSubscription = false;
  @observable inSpace = null;
  @observable isEarlyAccess;

  @computed get spaceType() {
    return this.__typename.toLowerCase();
  }

  @computed get spaceSlug() {
    if (this.spaceType === 'profile') {
      return this.username;
    }
    if (this.spaceType === 'initiative') {
      return this.slug;
    }
    return '';
  }

  @computed get requiresPaymentMethod() {
    return (
      !this.hasValidPaymentMethod ||
      (this.preview.totalAmount !== 0 &&
        this.preview.totalAmount >= this.preview.discountAmount)
    );
  }
}
ModelRegistry.add(PaymentSpaceModel);

export class PaymentSpaceApp extends App {
  static registryName = 'PaymentSpaceApp';
  static AppProps = {
    billingSubscriptions: 'SubscriptionsArrayApp',
    subscriptionPreview: 'SubscriptionPreviewApp',
    creditCards: 'CreditCardArrayApp',
    seatsUsage: 'SeatsUsageApp',
    invoices: 'InvoiceArrayApp',
  };

  static Model = PaymentSpaceModel;

  @observable coupon = '';
  @observable couponFieldVisible = false;
  @observable subscribing = false;
  @observable countryCode = '';
  @observable taxId = '';
  // @observable creditCards = [];

  @observable form = null;
  @observable couponForm = null;

  @observable extraSeats = '';
  @observable addingExtraSeats = false;
  @observable subscribing = false;
  @observable loading = false;
  @observable reactivating = false;

  @action UpdateExtraSeats = extraSeats => {
    this.extraSeats = extraSeats || '';
  };

  @action SetAddingExtraSeats = bool => {
    this.addingExtraSeats = bool;
  };

  @action SetSubscribing = bool => {
    this.subscribing = bool;
  };

  @action Reactivate = () => {
    this.manager.SubmitSubscription(this, {
      onSysErr: this.OnReactivateError,
      onUserErr: this.OnReactivateError,
      onSuccess: this.OnReactivateSuccess,
    });
  };

  @action Subscribe = (options = {}) => {
    if (this.form && this.preview.finalAmount !== 0 && !this.form.isValid) {
      this.form.alert();
      return;
    }
    if (this.form) this.form.SetPosting(true);
    this.SetSubscribing(true);
    this.manager.SubmitSubscription(this, {
      onSysErr: err => {
        this.OnSubscribeError(err);
        if (options.onSysErr) options.onSysErr(err);
      },
      onUserErr: err => {
        this.OnSubscribeError(err);
        if (options.onUserErr) options.onUserErr(err);
      },
      onSuccess: result => {
        this.OnSubscribeSuccess.bind(this)(result);
        if (options.onSuccess) options.onSuccess(result);
      },
    });
  };

  ValidateSCA = async () => {
    if (!this.requiresSCAValidation) return;
    const paymentMethodId = this.defaultCreditCard?.stripeId;
    if (!paymentMethodId) {
      Alert.error(
        i18next.t(
          'No credit card found. Please add a credit card before validation.',
        ),
      );
      return;
    }
    const validationSucess = await this.HandleSCA(
      this.subscription.scaSecret,
      paymentMethodId,
    );
    if (validationSucess) {
      this.RmScaSecret();
    }
  };

  @action RmScaSecret() {
    this.subscription.scaSecret = null;
  }

  HandleSCA = async (scaSecret, paymentMethodId) => {
    if (!scaSecret) return true;
    const { stripe } = stripeStore;
    if (!stripe) {
      const errorMessage = 'There was an error connecting to Stripe.';
      analytics.logError(new Error(errorMessage));
      return false;
    }
    let data;
    if (paymentMethodId) {
      data = { payment_method: paymentMethodId };
    }
    const { error } = await stripe.handleCardPayment(scaSecret, data);
    if (error) {
      const errorMessage = 'There was an error validating this transaction';
      Alert.error(i18next.t(errorMessage));
      analytics.logError(new Error(errorMessage));
      return false;
    }
    return true;
  };

  @action OnReactivateError = () => {
    Alert.error(
      i18next.t(
        'Hmm... We failed re-activate your Subscription. Please try again.',
      ),
    );
  };

  @action OnReactivateSuccess = () => {
    this.manager.NewSubscriptionQuery();
    Alert.success(
      i18next.t(
        'Your Wikifactory Subscription has been re-activated. Welcome back!',
      ),
    );
  };

  @action OnCancelError = () => {
    Alert.error(
      i18next.t('Hmm... We failed downgrade you to Free. Please try again.'),
    );
    this.loading = false;
  };

  @action OnCancelSuccess = () => {
    this.manager.NewSubscriptionQuery();
    Alert.success(i18next.t('You have successful cancelled the subscription.'));
  };

  @action OnSubscribeError = errs => {
    const nonValidationErrs = this.form.SetServerErrs(errs);
    if (this.form) this.form.SetPosting(false);
    this.SetSubscribing(false);
    if (nonValidationErrs.length !== 0) {
      Alert.error(
        i18next.t(
          'Hmm... We failed activate your Subscription. Please try again.',
        ),
      );
    }
  };

  OnSubscribeSuccess = async result => {
    const { scaSecret, nextAmount } = result;
    await this.HandleSCA(scaSecret);
    if (this.form) {
      this.form.SetPosting(false);
      this.form.clearDraft();
    }
    this.SetSubscribing(false);
    const purchaseData = nextAmount
      ? { value: nextAmount / 100.0, currency: 'EUR' }
      : {};
    analytics.sendEvent('Purchase', purchaseData, {
      twitter: true,
      facebook: true,
    });
    analytics.linkedinPixel('1169177');
    Alert.success(
      i18next.t(
        'Your Wikifactory Subscription has been activated. Congrats and enjoy!',
      ),
    );
  };

  @action ClaimCoupon = () => {
    this.manager.NewSubscriptionQuery({ preview: true });
    this.ConfirmSubscribe();
  };

  @action SetCoupon = coupon => {
    this.coupon = coupon;
  };

  @action SetCouponFieldVisible = couponFieldVisible => {
    if (couponFieldVisible) {
      if (this.couponForm) {
        this.coupon = '';
      } else {
        this.InitCouponForm();
      }
    }
    this.couponFieldVisible = couponFieldVisible;
  };

  @action InitVATForm = () => {
    const formConf = {
      fields: {
        countryCode: {
          appKey: 'value',
          labelKey: 'label',
          label: 'Country',
          placeholder: 'Please select',
          component: 'forms.SelectField',
          fieldStore: 'forms.StringFieldStore',
          selectionComponent: 'payment.countrySelection',
          draftDisabled: true,
          options: {
            optionComponent: 'payment.countryOption',
            values: countriesValues,
          },
        },
      },
      checkout: {
        title: 'Confirm subscription',
        buttonText: 'Subscribe now',
        buttonHandler: 'Subscribe',
        buttonTextProcessing: 'Subscribing...',
        fields: ['countryCode', 'taxId'],
      },
    };

    this.form = new FormStore(this, formConf, 'checkout', false);
  };

  @action InitCouponForm = () => {
    const formConf = {
      fields: {
        coupon: {
          label: 'Coupon',
          draftDisabled: true,
        },
      },
      coupon: {
        fields: ['coupon'],
        buttonText: 'Claim',
      },
    };
    this.couponForm = new FormStore(this, formConf, 'coupon', false);
  };

  @action OnReactivateSubscriptionError = () => {
    Alert.error(
      i18next.t(
        'Hmm... We failed to reactivate your subscription. Please try again.',
      ),
    );
    this.reactivating = false;
  };

  @action CancelSubscription = successCallback => {
    this.loading = true;
    this.manager.CancelSubscription(this, {
      onSysErr: this.OnCancelError,
      onUserErr: this.OnCancelError,
      onSuccess: result => {
        this.OnCancelSuccess(result);
        modalStore.Close();
        this.loading = false;
        if (successCallback) {
          successCallback();
        }
      },
    });
  };

  @action ReactivateSubscription = () => {
    this.reactivating = true;
    this.manager.ReactivateSubscription(this, {
      onSysErr: this.OnReactivateSubscriptionError,
      onUserErr: this.OnReactivateSubscriptionError,
      onSuccess: result => {
        this.OnReactivateSuccess(result);
        this.reactivating = false;
      },
    });
  };

  @action ConfirmCancel = () => {
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () =>
        this.manager.CancelSubscription(this, {
          onSysErr: this.OnCancelError,
          onUserErr: this.OnCancelError,
          onSuccess: result => {
            this.OnCancelSuccess(result);
            modalStore.Close();
          },
        }),
      title: 'Are you sure about cancel subscription?',
      actionName: 'Proceed',
      undo: true,
    });
  };

  @action ConfirmSubscribe = options => {
    const ConfirmSubscription = ComponentRegistry.get(
      'modals.ConfirmSubscription',
    );
    modalStore.SetModalComponent(ConfirmSubscription, {
      appManager: this.manager,
      hideOnlyOnEsc: true,
      confirmAction: () => {
        this.manager.paymentSpacePreview.SetCoupon(this.coupon);
        return this.manager.paymentSpacePreview.Subscribe({
          onSuccess: result => {
            modalStore.Close();
            this.manager.NewSubscriptionQuery();
            if (options.onSuccess) options.onSuccess(result);
          },
          onSysError: result => {
            if (options.onError) options.onError(result);
          },
          onUserError: result => {
            if (options.onError) options.onError(result);
          },
        });
      },
    });
  };

  @computed get requiresSCAValidation() {
    return !!(this.subscription && this.subscription.requiresSCAValidation);
  }

  @computed get subscription() {
    const subscription =
      this.billingSubscriptions && this.billingSubscriptions.index(0);
    return subscription || null;
  }

  @computed get couponApplied() {
    return !!this.coupon;
  }

  @computed get preview() {
    if (this.subscription) {
      if (this.subscription.addSeatsPreview) {
        return this.subscription.addSeatsPreview;
      }
      if (this.subscription.removeSeatsPreview) {
        return this.subscription.removeSeatsPreview;
      }
    }
    return this.subscriptionPreview || null;
  }

  @computed get defaultCreditCard() {
    if (!(this.creditCards && this.creditCards.apps)) return null;
    return (
      this.creditCards.apps.find(creditCard => creditCard.isDefault) || null
    );
  }
}
AppRegistry.add(PaymentSpaceApp);
